import React, { useMemo, useState, useEffect } from 'react';
import {
  buildShareLinkMessage,
  getShareSenderName,
  shareOrCopyLink,
  SHARE_OUTCOME_COPIED,
} from '../../../util/shareLink';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IconCollection, ResponsiveImage, Map, NamedLink } from '../../../components';
import Tooltip from '../../../components/ToolTip/ToolTip';
import { calculateDistance, ensureCurrentUser } from '../../../util/data';
import { types as sdkTypes } from 'sharetribe-flex-sdk';
import { useConfiguration } from '../../../context/configurationContext';
import {
    EXACT_LOCATION_ZOOM,
    buildLocationMapsConfig,
    resolveStoreAddress,
    shouldShowExactLocation,
    useMapboxReady,
} from '../../../components/Map/locationMapPresentation';
import css from './Header.module.css';
import StorefrontTabs from './StorefrontTabs';
import IntroText from '../IntroText/IntroText';
import StoreHoursRow from '../StoreHoursRow/StoreHoursRow';
import { getStoreTimeZone } from '../../StoreHoursPage/StoreHoursForm/storeHoursUtils';
import { STORE_COVER_HEIGHT, STORE_COVER_WIDTH } from '../CustomizeStoreCoverModal/storeCoverConstants';
import { propTypes } from '../../../util/types';
import { parse } from '../../../util/urlHelpers';

const { LatLng } = sdkTypes;

/**
 * Applies Cloudinary width, crop, format, and quality transforms to a Cloudinary secure_url.
 * Inserts the transform string immediately after '/upload/' in the URL path.
 * Safe to call with any string — returns the original URL unchanged if '/upload/' is not found.
 *
 * @param {string} url - A Cloudinary secure_url
 * @param {number} [width=1400] - Target width in pixels
 * @returns {string}
 */
const buildCloudinaryBannerUrl = (url, width = 1400) => {
    if (!url || typeof url !== 'string' || !url.includes('/upload/')) return url || '';
    return url.replace('/upload/', `/upload/w_${width},c_fill,f_auto,q_auto/`);
};

/**
 * Verification Badges Component with Tooltips
 */
const VerificationBadges = ({ usersEmailVerified, isIdVerified, showVerifyCta, onVerifyClick, className }) => {
    const intl = useIntl();
    const { isEmailVerified = false, isStripeVerified = false, isCompletedTransitions = false, isPhoneVerified = false } = usersEmailVerified || {};
    const shouldShowVerificationTooltip = isEmailVerified || isStripeVerified || isCompletedTransitions || isPhoneVerified;

    const verificationTooltipContent = () => {
        return (
            <div className={css.verificationTooltip}>
                <div className={css.verificationTooltipHeader}>
                    <span className={css.verificationTooltipBadge}>
                        <IconCollection name="verifiedIcon" />
                    </span>
                    <div>
                        <p className={css.verificationTooltipTitle}>
                            {intl.formatMessage({
                                id: 'DetailCardImage.accountVerifiedTitle',
                                defaultMessage: 'Account Verified',
                            })}
                        </p>
                    </div>
                </div>
                <ul className={css.verificationTooltipList}>
                    {isEmailVerified && (
                        <li className={css.verificationTooltipItem}>
                            <span className={css.verificationTooltipIcon} aria-hidden="true">
                                <i className="ri-mail-check-line" />
                            </span>
                            {intl.formatMessage({
                                id: 'DetailCardImage.accountVerifiedEmail',
                                defaultMessage: 'Email has been verified',
                            })}
                        </li>
                    )}
                    {isPhoneVerified && (
                        <li className={css.verificationTooltipItem}>
                            <span className={css.verificationTooltipIcon} aria-hidden="true">
                                <i className="ri-phone-line" />
                            </span>
                            {intl.formatMessage({
                                id: 'DetailCardImage.accountVerifiedPhone',
                                defaultMessage: 'Mobile number has been verified',
                            })}
                        </li>
                    )}
                    {isStripeVerified && (
                        <li className={css.verificationTooltipItem}>
                            <span className={css.verificationTooltipIcon} aria-hidden="true">
                                <i className="ri-secure-payment-line" />
                            </span>
                            {intl.formatMessage({
                                id: 'DetailCardImage.accountVerifiedPayment',
                                defaultMessage: 'Payment method verified by Stripe',
                            })}
                        </li>
                    )}
                    {isCompletedTransitions && (
                        <li className={css.verificationTooltipItem}>
                            <span className={css.verificationTooltipIcon} aria-hidden="true">
                                <i className="ri-medal-line" />
                            </span>
                            {intl.formatMessage({
                                id: 'DetailCardImage.accountVerifiedRental',
                                defaultMessage: 'Successfully completed a rental',
                            })}
                        </li>
                    )}
                </ul>
                <p className={css.verificationTooltipFooter}>
                    {intl.formatMessage({
                        id: 'DetailCardImage.accountVerifiedFooter',
                        defaultMessage:
                            'This account has completed the above verification steps to ensure safer, more trusted transactions.',
                    })}
                </p>
            </div>
        );
    };

    const idVerificationTooltipContent = (
        <div className={css.verificationTooltip}>
            <div className={css.verificationTooltipHeader}>
                <span className={classNames(css.verificationTooltipBadge, css.verificationTooltipBadgeInfo)}>
                    <i className="ri-shield-user-fill" aria-hidden="true" />
                </span>
                <p className={css.verificationTooltipTitle}>
                    {intl.formatMessage({
                        id: 'DetailCardImage.visualIdTitle',
                        defaultMessage: 'Visual ID Verified',
                    })}
                </p>
            </div>
            <div className={css.verificationTooltipItem}>
                <span className={classNames(css.verificationTooltipIcon, css.verificationTooltipIconAlert)} aria-hidden="true">
                    <i className="ri-user-line" />
                </span>
                {intl.formatMessage({
                    id: 'DetailCardImage.visualIdSubtitle',
                    defaultMessage: 'User has completed live ID verification',
                })}
            </div>
            <p className={css.verificationTooltipDescription}>
                {intl.formatMessage({
                    id: 'DetailCardImage.visualIdDescription',
                    defaultMessage:
                        "We've confirmed this user's identity through a secure, government ID and live visual check to help keep our community safe.",
                })}
            </p>
        </div>
    );

    const showAnyBadge = shouldShowVerificationTooltip || isIdVerified || (showVerifyCta && !isIdVerified && onVerifyClick);
    if (!showAnyBadge) {
        return null;
    }

    return (
        <div className={classNames(css.verificationBadges, className)}>
            {shouldShowVerificationTooltip ? (
                <Tooltip placement="bottom" mode="light" size="xl" content={verificationTooltipContent()}>
                    <span className={classNames(css.verificationBadge, css.verificationBadgeVerified)}>
                        <i className="ri-verified-badge-fill" />
                    </span>
                </Tooltip>
            ) : null}
            {isIdVerified ? (
                <Tooltip placement="bottom" mode="light" size="xl" content={idVerificationTooltipContent}>
                    <span className={classNames(css.verificationBadge, css.verificationBadgeId)}>
                        <i className="ri-shield-user-fill" aria-hidden="true" />
                    </span>
                </Tooltip>
            ) : null}
            {!isIdVerified && showVerifyCta && onVerifyClick ? (
                <button
                    type="button"
                    className={classNames(css.verificationBadge, css.verificationBadgeCta)}
                    onClick={onVerifyClick}
                    aria-label={intl.formatMessage({
                        id: 'TopbarDesktop.verifyIdentity',
                        defaultMessage: 'Verify Your Identity',
                    })}
                >
                    <i className="ri-shield-line" aria-hidden="true" />
                    <FormattedMessage
                        id="TopbarDesktop.verifyIdentity"
                        defaultMessage="Verify Your Identity"
                    />
                </button>
            ) : null}
        </div>
    );
};

/**
 * Header Component for Store Profile Page
 */
const Header = props => {
    const intl = useIntl();
    const history = useHistory();
    const location = useLocation();
    const config = useConfiguration();
    const {
        user,
        rating,
        reviews,
        queryReviewsInProgress,
        totalReviews,
        onShowReviews,
        currentUserLocationInfo,
        storeUrl,
        onContactUser,
        currentUser,
        onManageDisableScrolling,
        locationSearch,
        locationPathname,
        activeTab = 'home',
        isCurrentUser = false,
        onVerifyIdentityClick,
        onVerifyIdentityClose,
        isCustomizeCoverOpen,
        onOpenCustomizeCover,
        onCloseCustomizeCover,
        storeCoverImage,
        storeCoverBackgroundColor,
        storeBlogs = [],
        storeBlogsFetchCompleted = false,
    } = props;

    const [linkCopied, setLinkCopied] = useState(false);
    const isMapboxReady = useMapboxReady();
    const [hasMapOpen, setHasMapOpen] = useState(false);
    const [contactPopupOpen, setContactPopupOpen] = useState(false);
    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const isLoggedIn = !!ensuredCurrentUser?.id;

    const { profileImage } = user || {};
    const { displayName, publicData, abbreviatedName } = user?.attributes?.profile || {};

    const { city, state, country: countryCode, origin, storeName, storeAddress, storeDisplayPhoto } = publicData || {};
    const getLocalizedCountryName = code => {
        if (!code || !code.trim()) return null;
        try {
            if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
                const language = intl.locale || 'en';
                const regionNames = new Intl.DisplayNames([language], { type: 'region' });
                return regionNames.of(code) || code;
            }
        } catch (e) {
            // Fall through to raw ISO code when DisplayNames is unavailable.
        }
        return code;
    };
    const countryName = getLocalizedCountryName(countryCode);

    // Get store address fields if available, otherwise use profile address
    const storeCity = storeAddress?.city || city;
    const storeState = storeAddress?.state || state;
    const storeCountry = storeAddress?.country || countryCode;
    const storeCountryName = getLocalizedCountryName(storeCountry);

    const displayStoreName = !!storeName ? storeName : displayName;
    const usersEmailVerified = props.usersEmailVerified || {};
    const isIdVerified = !!publicData?.isUserVerified;

    // Fix: Photo fallback logic - storeDisplayPhoto -> profileImage -> name initials
    const displayPhoto = storeDisplayPhoto || profileImage;
    const showInitials = !storeDisplayPhoto && !profileImage;
    const initials = showInitials
        ? (abbreviatedName || displayStoreName?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'S')
        : null;

    // Fix: Use store address if available, otherwise profile address
    const storeLocation = storeAddress?.origin || origin;
    const storeTimeZone = getStoreTimeZone(storeLocation);

    // Map configuration. The store owner can publish their exact address from
    // Store Settings; falls back to the privacy circle when the address is missing.
    // This only ever read storeAddress.formattedAddress (or storeAddress-as-a-string).
    // Store addresses are actually saved as { address, city, state, postcode, country,
    // origin } with no formattedAddress key at all (StoreBasicInformationPage.duck.js),
    // so this resolved to '' for essentially every real store - which made
    // shouldShowExactLocation below permanently false and is why the address never
    // appeared, even for owners who had opted in. resolveStoreAddress reads the real
    // shape and falls back to the profile address for "same as personal" stores and for
    // stores that only ever filled in a profile address.
    const storeMapAddress = resolveStoreAddress(publicData);
    const showExactLocation = shouldShowExactLocation(
        publicData?.showAddressInStore,
        storeMapAddress
    );
    const mapMapsConfig = buildLocationMapsConfig(config.maps, showExactLocation);

    // Static image first, then upgrade to the interactive map once Mapbox GL has
    // loaded so the +/- zoom controls become usable.
    const useStaticMap = !isMapboxReady;

    const mapProps = {
        address: storeMapAddress,
        center: storeLocation, // Used for circle center
        obfuscatedCenter: storeLocation, // Required when fuzzy.enabled
        mapsConfig: mapMapsConfig,
        useStaticMap,
        showFuzzyCircleInitially: !showExactLocation,
        ...(showExactLocation ? { zoom: EXACT_LOCATION_ZOOM } : {}),
    };

    // Calculate distance if user location and store location are available
    const distanceInfo = useMemo(() => {
        if (!currentUserLocationInfo?.origin || !storeLocation) return null;

        const { lat: userLat, lng: userLng } = currentUserLocationInfo.origin;
        const { lat: storeLat, lng: storeLng } = storeLocation;

        if (!userLat || !userLng || !storeLat || !storeLng) return null;

        return calculateDistance(userLat, userLng, storeLat, storeLng);
    }, [currentUserLocationInfo, storeLocation]);

    const showInMiles = currentUserLocationInfo?.country === 'US';
    const distance = distanceInfo ? (showInMiles ? distanceInfo.miles : distanceInfo.kilometers) : null;

    // Location text - Fix: Show distance, city, state/region, and country
    const locationParts = [];
    if (distance) {
        locationParts.push(
            showInMiles
                ? intl.formatMessage({ id: 'ListingPageHeader.milesAway', defaultMessage: '{distance} miles away' }, { distance })
                : intl.formatMessage({ id: 'ListingPageHeader.kmAway', defaultMessage: '{distance} km away' }, { distance })
        );
    }
    // This toggle button stays the coarse city/state/country summary in every case. The
    // full street address now renders under the map instead (see .mapAddressRow below),
    // where the owner opted in - previously the address was substituted into this button
    // and never shown under the map at all.
    if (storeCity) locationParts.push(storeCity);
    if (storeState && storeCity !== storeState) locationParts.push(storeState);
    if (storeCountryName && storeCity !== storeCountryName && storeState !== storeCountryName) {
        locationParts.push(storeCountryName);
    }

    const locationText = locationParts.length > 0 ? locationParts.join(', ') : null;
    const locationDistanceText = distance && locationParts.length > 0 ? locationParts[0] : null;
    const locationRemainderText = locationParts.length > 1 ? locationParts.slice(1).join(', ') : null;

    const buildStoreTabUrl = (tabKey) => {
        if (!storeUrl) return '';
        const [withoutHash] = storeUrl.split('#');
        const [base] = withoutHash.split('?');
        const cleanBase = base.replace(/\/+$/, '');
        return tabKey === 'home' ? cleanBase : `${cleanBase}/${tabKey}`;
    };


    // Handle scroll to map
    const handleLocationClick = (e) => {
        e.preventDefault();
        const mapElement = typeof document !== 'undefined' ? document.getElementById('store-map') : null;
        if (mapElement) {
            mapElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Handle reviews click — same tab, preserve query (e.g. filters)
    const handleReviewsClick = (e) => {
        e.preventDefault();
        // Open Reviews tab in a new tab (full-page layout)
        if (typeof window !== 'undefined' && storeUrl) {
            const url = buildStoreTabUrl('reviews');
            window.open(url, '_blank', 'noopener,noreferrer');
            return;
        }
        // Fallback (legacy): if a reviews section exists on page, scroll to it
        const reviewsElement = typeof document !== 'undefined' ? document.getElementById('reviews-section') : null;
        if (reviewsElement) reviewsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Share: the store link with the "has shared a link with you" message above it
    // (util/shareLink.js, client request 2026-09-18) - native share sheet where there
    // is one, otherwise the whole text is copied and "Link copied" shows briefly.
    const handleShare = async () => {
        if (typeof window === 'undefined' || !storeUrl) return;
        const outcome = await shareOrCopyLink({
            title: intl.formatMessage({ id: 'ShareLink.storeTitle' }),
            message: buildShareLinkMessage(intl, {
                senderName: getShareSenderName(currentUser),
                siteName: config.marketplaceName,
            }),
            url: storeUrl,
        });
        if (outcome === SHARE_OUTCOME_COPIED) {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        }
    };

    // Fix: Phone number - show 1-888-234-RENT
    const handlePhoneClick = () => {
        window.location.href = 'tel:1-888-234-RENT';
    };

    // Fix: Email - open mailto
    const handleMessageClick = () => {
        window.location.href = 'mailto:hey@rentanythingstore.com';
    };

    // Handle contact button click
    const handleContactClick = () => {
        setContactPopupOpen(!contactPopupOpen);
    };

    // Close popup when clicking outside
    const handlePopupClickOutside = (e) => {
        if (e.target === e.currentTarget) {
            setContactPopupOpen(false);
        }
    };

    // Close popup when clicking outside or pressing escape key
    useEffect(() => {
        const handleClickOutside = (event) => {
            // event.target may not support .closest in older/in-app browsers (SVG elements,
            // very old Firefox/Chrome). Guard with optional chaining; treat a missing
            // .closest as "clicked outside" so the popup still closes correctly.
            const isInside = typeof event.target?.closest === 'function'
                ? event.target.closest(`.${css.contactButtonWrapper}`)
                : false;
            if (contactPopupOpen && !isInside) {
                setContactPopupOpen(false);
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && contactPopupOpen) {
                setContactPopupOpen(false);
            }
        };

        if (contactPopupOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [contactPopupOpen]);

    const hasCoverImage = storeCoverImage?.secure_url;
    const hasCoverColor = storeCoverBackgroundColor && !hasCoverImage;
    const coverBackgroundStyles = {
        '--store-cover-width': STORE_COVER_WIDTH,
        '--store-cover-height': STORE_COVER_HEIGHT,
        backgroundImage: hasCoverImage ? `url(${storeCoverImage.secure_url})` : undefined,
        backgroundColor: hasCoverColor ? storeCoverBackgroundColor : undefined,
    };

    const rawBannerUrl = storeCoverImage?.secure_url || storeDisplayPhoto?.secure_url || '';
    const bannerSrc = buildCloudinaryBannerUrl(rawBannerUrl, 1400);
    const bannerSrcSet = rawBannerUrl
        ? [700, 1200, 1400]
              .map(w => `${buildCloudinaryBannerUrl(rawBannerUrl, w)} ${w}w`)
              .join(', ')
        : undefined;

    return (
        <>
            <div className={css.headerWrapper}>
                {(hasCoverImage || hasCoverColor) ? (
                    <div
                        className={css.mobileBannerWrapper}
                        style={coverBackgroundStyles}
                    >
                        {hasCoverImage && (
                            <img
                                src={bannerSrc}
                                srcSet={bannerSrcSet || undefined}
                                sizes="(max-width: 700px) 700px, (max-width: 1200px) 1200px, 1400px"
                                alt={storeCoverImage?.seoTags?.altText || `${displayStoreName} store banner`}
                                title={storeCoverImage?.seoTags?.titleTag || ''}
                                width={1400}
                                height={400}
                                loading="eager"
                                decoding='async'
                                fetchpriority="high"
                                style={{
                                    position: 'absolute',
                                    width: '1px',
                                    height: '1px',
                                    overflow: 'hidden',
                                    clip: 'rect(0,0,0,0)',
                                    whiteSpace: 'nowrap',
                                    border: 0,
                                }}
                            />
                        )}
                    </div>
                ) : null}
                {/* {hasCoverImage && storeCoverImage?.seoTags?.caption && (
                    <p className={css.bannerCaption}>
                        {storeCoverImage.seoTags.caption}
                    </p>
                )} */}
                {/* {(hasCoverImage || hasCoverColor) ? (
                    <div
                        className={css.headerCoverLayer}
                        style={{
                            backgroundImage: hasCoverImage ? `url(${storeCoverImage.secure_url})` : undefined,
                            backgroundColor: hasCoverColor ? storeCoverBackgroundColor : undefined,
                        }}
                        aria-hidden
                    />
                ) : null} */}
                <header
                    className={classNames(
                        css.header,

                        (hasCoverImage || hasCoverColor) && css.headerWithCover
                    )}
                    style={coverBackgroundStyles}
                >
                    <div className={css.headerContent}>

                        <div className={css.topContainer}>
                            {/* Store Logo - Top Left */}
                            <div className={css.logoContainer}>
                                {displayPhoto ? (
                                    storeDisplayPhoto ? (
                                        // Store display photo (Cloudinary object)
                                        <ResponsiveImage
                                            className={css.logo}
                                            renderCloudinaryImage={{
                                                secure_url: storeDisplayPhoto.secure_url,
                                                alt: storeDisplayPhoto?.seoTags?.altText || displayStoreName || '',
                                                variantsToRender: ['square-small', 'square-small2x'],
                                                defaultVariant: 'square-small',
                                            }}
                                            alt={storeDisplayPhoto?.seoTags?.altText || displayStoreName || ''}
                                            title={storeDisplayPhoto?.seoTags?.titleTag || ''}
                                            loading="eager"
                                            fetchPriority="high"
                                            width={storeDisplayPhoto?.width  || 240}
                                            height={storeDisplayPhoto?.height || 240}
                                        />
                                    ) : (
                                        // Profile photo (SDK image object)
                                        <ResponsiveImage
                                            className={css.logo}
                                            image={profileImage}
                                            alt={displayStoreName || ''}
                                            variants={['square-small', 'square-small2x']}
                                            abbreviatedName={abbreviatedName}
                                        />
                                    )
                                ) : (
                                    // Fallback to initials
                                    <div className={css.logoPlaceholder}>
                                        <span className={css.logoPlaceholderText}>
                                            {initials}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons - Top Right - Only show if logged in */}
                            {isLoggedIn ? (
                                <div className={css.actionButtons}>
                                    <div className={css.contactButtonWrapper}>
                                        <button
                                            type="button"
                                            className={classNames(css.actionButton, css.contactButton)}
                                            onClick={handleContactClick}
                                            aria-label={intl.formatMessage({ id: 'StoreProfilePage.contact', defaultMessage: 'Contact store' })}
                                        >
                                            <FormattedMessage id="StoreProfilePage.contactButton" defaultMessage="Contact" />
                                        </button>
                                        {contactPopupOpen && (
                                            <div className={css.popup} onClick={handlePopupClickOutside}>
                                                <div className={css.popupContent}>
                                                    <button
                                                        type="button"
                                                        className={css.actionButton}
                                                        onClick={handlePhoneClick}
                                                        aria-label={intl.formatMessage({ id: 'StoreProfilePage.call', defaultMessage: 'Call store' })}
                                                    >
                                                        <i className="ri-phone-line" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={css.actionButton}
                                                        onClick={handleMessageClick}
                                                        aria-label={intl.formatMessage({ id: 'StoreProfilePage.message', defaultMessage: 'Message store' })}
                                                    >
                                                        <i className="ri-mail-send-line" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className={css.actionButton}
                                        onClick={handlePhoneClick}
                                        aria-label={intl.formatMessage({ id: 'StoreProfilePage.call', defaultMessage: 'Call store' })}
                                    >
                                        <i className="ri-phone-line" />
                                    </button>
                                    <button
                                        type="button"
                                        className={css.actionButton}
                                        onClick={handleMessageClick}
                                        aria-label={intl.formatMessage({ id: 'StoreProfilePage.message', defaultMessage: 'Message store' })}
                                    >
                                        <i className="ri-mail-send-line" />
                                    </button>
                                    <button
                                        type="button"
                                        className={classNames(css.actionButton, css.shareButton)}
                                        onClick={handleShare}
                                        aria-label={intl.formatMessage({ id: 'StoreProfilePage.share', defaultMessage: 'Share store' })}
                                    >
                                        <i className="ri-share-line" />
                                        {linkCopied && (
                                            <span className={css.linkCopiedText}>
                                                <FormattedMessage id="StoreProfilePage.linkCopied" defaultMessage="Link copied" />
                                            </span>
                                        )}
                                    </button>
                                    {isCurrentUser ? (
                                        <>
                                            <button
                                                type="button"
                                                className={classNames(css.actionButton, css.updateBannerButton)}
                                                onClick={onOpenCustomizeCover}
                                                aria-label={intl.formatMessage({ id: 'StoreProfilePage.updateBanner', defaultMessage: 'Update Banner' })}
                                            >
                                                <i className="ri-image-edit-line" aria-hidden />
                                                <span className={css.actionButtonLabel}>
                                                    <FormattedMessage id="StoreProfilePage.updateBanner" defaultMessage="Update Banner" />
                                                </span>
                                            </button>
                                            <NamedLink
                                                name="StoreBasicInformationPage"
                                                className={classNames(css.actionButton, css.manageStoreButton)}
                                            >
                                                <i className="ri-settings-3-line" aria-hidden />
                                                <span className={css.actionButtonLabel}>
                                                    <FormattedMessage id="StoreProfilePage.manageStore" defaultMessage="Manage Store" />
                                                </span>
                                            </NamedLink>
                                        </>
                                    ) : null}
                                </div>
                            ) : <div className={css.actionButtons}> </div>}
                        </div>

                        {/* Store Info - Bottom Left */}
                        <div className={css.storeInfo}>
                            <div className={css.storeNameRow}>
                                <h1 className={css.storeName}>{displayStoreName}  <VerificationBadges
                                    usersEmailVerified={usersEmailVerified}
                                    isIdVerified={isIdVerified}
                                    showVerifyCta={isCurrentUser}
                                    onVerifyClick={onVerifyIdentityClick}
                                    className={css.verificationBadges}
                                /></h1>

                            </div>

                            <div className={css.storeMeta}>
                                {/* Rating and Reviews */}
                                {rating !== null && rating !== undefined && rating > 0 && (
                                    <button
                                        type="button"
                                        className={css.ratingButton}
                                        onClick={handleReviewsClick}
                                        aria-label={intl.formatMessage({ id: 'StoreProfilePage.viewReviews', defaultMessage: 'View reviews' })}
                                    >
                                        <i className="ri-star-fill" />
                                        <span className={css.ratingValue}>{rating.toFixed(1)}</span>
                                        <span className={css.reviewsCount}>
                                            {queryReviewsInProgress ? (
                                                <FormattedMessage
                                                    id="StoreProfilePage.loadingReviews"
                                                    defaultMessage="Loading reviews..."
                                                />
                                            ) : totalReviews === 0 ? (
                                                <FormattedMessage
                                                    id="StoreProfilePage.noReviews"
                                                    defaultMessage="No reviews yet"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    id="StoreProfilePage.reviewsCount"
                                                    defaultMessage="({count} reviews)"
                                                    values={{ count: totalReviews }}
                                                />
                                            )}
                                        </span>
                                    </button>
                                )}

                                {/* Location */}
                                {locationText && (
                                    <button
                                        type="button"
                                        className={css.locationButton}
                                        onClick={handleLocationClick}
                                        aria-label={intl.formatMessage({ id: 'StoreProfilePage.scrollToMap', defaultMessage: 'Scroll to map' })}
                                    >
                                        <i className="ri-map-pin-2-line" />
                                        {locationDistanceText ? (
                                            <span>
                                                <strong className={css.locationDistance}>{locationDistanceText}</strong>
                                                {locationRemainderText ? (
                                                    <span className={css.locationRemainder}>, {locationRemainderText}</span>
                                                ) : null}
                                            </span>
                                        ) : (
                                            <span>{locationText}</span>
                                        )}
                                        <span className={css.mapToggle} onClick={() => setHasMapOpen(!hasMapOpen)}>
                                            <FormattedMessage
                                                id="StoreProfilePage.Map"
                                                defaultMessage="Map"
                                            /> {hasMapOpen ? <i className="ri-arrow-up-s-line" /> : <i className="ri-arrow-down-s-line" />}
                                        </span>
                                    </button>
                                )}
                                {hasMapOpen && <div className={css.mapContainer}>
                                    {storeLocation && storeLocation.lat && storeLocation.lng && (
                                        <>
                                            <Map
                                                key={`store-header-map-${storeLocation.lat}-${storeLocation.lng}-${useStaticMap ? 'static' : 'dynamic'}-${showExactLocation ? 'exact' : 'fuzzy'}`}
                                                {...mapProps}
                                            />
                                            {showExactLocation ? (
                                                <div className={`${css.locationMessage} ${css.locationMessagePickup}`}>
                                                    <FormattedMessage
                                                        id="StoreLocationMap.pickupConfirmedMessage"
                                                        values={{
                                                            addressLine: (
                                                                <span className={css.pickupAddressLine}>
                                                                    <i className="ri-map-pin-2-line" aria-hidden="true" />
                                                                    <strong>{storeMapAddress}</strong>
                                                                </span>
                                                            ),
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className={css.locationMessage}>
                                                    <IconCollection className={css.infoIcon} name="info" />
                                                    <FormattedMessage
                                                        id="DeliveryPickupOptionsField.exactLocationAfterBooking"
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>}
                            </div>
                            {/* Store Hours: mobile/tablet only - desktop shows this in the Sidebar's
                                left column instead, so it isn't duplicated at >=1024px. */}
                            <div className={css.storeHoursRowWrapper}>
                                <StoreHoursRow storeHours={publicData?.storeHours} timeZone={storeTimeZone} />
                            </div>
                            {/* Store SEO/AIO description: only on Home tab */}
                            {activeTab === 'home' && (
                                <div className={css.introMobile}>
                                    <IntroText
                                        storeAbout={publicData?.storeAbout}
                                        seoDescription={publicData?.seoDescription}
                                        storeName={publicData?.storeName}
                                    />
                                </div>
                            )}
                            {/* Close headerContent */}
                        </div>
                    </div>

                </header>
            </div>
            <StorefrontTabs
                storeUrl={storeUrl}
                locationSearch={locationSearch || ''}
                locationPathname={locationPathname || ''}
                publicData={publicData}
                totalReviews={totalReviews}
                isCurrentUser={isCurrentUser}
                storeBlogs={storeBlogs}
                storeBlogsFetchCompleted={storeBlogsFetchCompleted}
            />
        </>
    );
};

Header.propTypes = {
    user: propTypes.user.isRequired,
    rating: PropTypes.number,
    reviews: PropTypes.arrayOf(propTypes.review),
    totalReviews: PropTypes.number,
    onShowReviews: PropTypes.func,
    currentUserLocationInfo: PropTypes.shape({
        origin: propTypes.latlng,
        country: PropTypes.string,
    }),
    storeUrl: PropTypes.string.isRequired,
    onContactUser: PropTypes.func,
    currentUser: propTypes.currentUser,
    onManageDisableScrolling: PropTypes.func,
    locationSearch: PropTypes.string,
    locationPathname: PropTypes.string,
    activeTab: PropTypes.oneOf(['home', 'about', 'reviews', 'gallery', 'blog', 'faqs']),
    datesSelected: PropTypes.bool,
    usersEmailVerified: PropTypes.shape({
        isEmailVerified: PropTypes.bool,
        isStripeVerified: PropTypes.bool,
        isCompletedTransitions: PropTypes.bool,
        isPhoneVerified: PropTypes.bool,
    }),
    isCurrentUser: PropTypes.bool,
    onVerifyIdentityClick: PropTypes.func,
    onVerifyIdentityClose: PropTypes.func,
    isCustomizeCoverOpen: PropTypes.bool,
    onOpenCustomizeCover: PropTypes.func,
    onCloseCustomizeCover: PropTypes.func,
    storeCoverImage: PropTypes.object,
    storeCoverBackgroundColor: PropTypes.string,
    storeBlogs: PropTypes.array,
    storeBlogsFetchCompleted: PropTypes.bool,
};

VerificationBadges.propTypes = {
    usersEmailVerified: PropTypes.shape({
        isEmailVerified: PropTypes.bool,
        isStripeVerified: PropTypes.bool,
        isCompletedTransitions: PropTypes.bool,
        isPhoneVerified: PropTypes.bool,
    }),
    isIdVerified: PropTypes.bool,
    showVerifyCta: PropTypes.bool,
    onVerifyClick: PropTypes.func,
    className: PropTypes.string,
};

export default Header;
